// React
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Servisni sloj
import { savePricingSchemeAPI } from "services/api/pricing/schemeApi";
import { PricingSchemeEditFormValues } from "services/api/pricing/schemeTypes";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";

// UI
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardStepBar from "ui/components/Wizard/WizardStepBar";

// Lokalne komponente
import SchemeBasicsStep from "./components/SchemeBasicsStep/SchemeBasicsStep";
import SchemeItemsStep from "./components/SchemeItemsStep/SchemeItemsStep";
import SchemeSummaryCard from "./components/SchemeSummaryCard/SchemeSummaryCard";

// Tipovi i konstante
import { SCHEME_MANDATORY_STEP_COUNT, SCHEME_STEP_LABELS } from "./consts";
import { PricingSchemeEditCardProps } from "./types";
import { SCHEME_BASICS_STEP_SCHEMA, SCHEME_ITEMS_STEP_SCHEMA } from "./validation";

/** Shema koja se provjerava prije prelaska s koraka. */
const STEP_SCHEMAS = [SCHEME_BASICS_STEP_SCHEMA, SCHEME_ITEMS_STEP_SCHEMA];

/**
 * Obrazac cjenovne sheme, korak po korak.
 *
 * Spremanje je jedno, na zadnjem koraku: backend prima cijelu shemu odjednom, sa svim
 * stavkama, pa bi spremanje po koraku ostavljalo grupu bez stavaka.
 */
const PricingSchemeEditCard: React.FC<PricingSchemeEditCardProps> = (
  props: PricingSchemeEditCardProps
) => {
  const { show, scheme, overridableKeys, onSuccess, onClose } = props;

  const initialFormValues = useMemo<PricingSchemeEditFormValues>(
    () => ({
      id: scheme?.id ?? null,
      name: scheme?.name ?? null,
      description: scheme?.description ?? null,
      isActive: scheme?.isActive ?? true,
      items: (scheme?.items ?? []).map((item) => ({
        keyCode: item.keyCode,
        adjustmentType: item.adjustmentType,
        value: item.value,
      })),
    }),
    [scheme]
  );

  const [formValues, setFormValues] = useState<PricingSchemeEditFormValues>(initialFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  // Dvoklik na „Nastavi" inače preskoči korak.
  const isProcessingRef = useRef<boolean>(false);

  // Modal ostaje montiran, pa se svako otvaranje mora vratiti na shemu s poslužitelja.
  useEffect(() => {
    if (!show) return;

    setFormValues(initialFormValues);
    setErrors({});
    setServerErrors([]);
    setStepCurrent(0);
  }, [show, initialFormValues]);

  /*----------------------------------------------------------------
      NAVIGACIJA
  ----------------------------------------------------------------*/

  const handleNextStep = useCallback(() => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;

    try {
      if (!validateYup(formValues as any, STEP_SCHEMAS[stepCurrent], setErrors)) {
        return;
      }

      setErrors({});
      setStepCurrent((step) => step + 1);
    } finally {
      isProcessingRef.current = false;
    }
  }, [formValues, stepCurrent]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const handleStepClick = useCallback((stepIndex: number) => {
    setStepCurrent(stepIndex);
  }, []);

  /*----------------------------------------------------------------
      SPREMANJE
  ----------------------------------------------------------------*/

  const handleFinish = useCallback(async () => {
    setServerErrors([]);

    // Koraci se smiju preskakati klikom na traku, pa se prije slanja vrte obje sheme.
    const failingStep = STEP_SCHEMAS.findIndex(
      (schema) => !validateYup(formValues as any, schema, setErrors)
    );

    if (failingStep >= 0) {
      setStepCurrent(failingStep);
      return;
    }

    setIsSaving(true);

    try {
      await savePricingSchemeAPI(formValues);
      onSuccess();
    } catch (error: any) {
      // Tvrde brane s backenda, npr. pokušaj pregazivanja nabavne cijene.
      const list = error?.response?.data?.errors;

      if (Array.isArray(list)) {
        setServerErrors(list.map((e: any) => e.description));
      }
    } finally {
      setIsSaving(false);
    }
  }, [formValues, onSuccess]);

  /*----------------------------------------------------------------
      PRIKAZ
  ----------------------------------------------------------------*/

  const steps = [
    <WizardStep key="basics" stepNumber={0} stepCurrent={stepCurrent}>
      <SchemeBasicsStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        scheme={scheme}
      />
    </WizardStep>,
    <WizardStep key="items" stepNumber={1} stepCurrent={stepCurrent}>
      <SchemeItemsStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        overridableKeys={overridableKeys}
      />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="full"
      position="side"
      intro={{
        content: (
          <SchemeSummaryCard
            formValues={formValues}
            tenantCount={scheme?.tenantCount ?? null}
          />
        ),
      }}
    >
      <ModalHeader
        title={scheme ? "Uređivanje grupe" : "Nova cjenovna grupa"}
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />

      {/* Traka stoji izvan tijela da ne odscrola sa sadržajem koraka. */}
      <div className="flex-shrink-0">
        <WizardStepBar
          stepLabels={SCHEME_STEP_LABELS}
          stepCurrent={stepCurrent}
          onStepClick={handleStepClick}
          firstStepCompleted={!!formValues.name}
          mandatoryCount={SCHEME_MANDATORY_STEP_COUNT}
        />
      </div>

      <ModalBody fixedFooterSpacing={false}>
        {/* Greška s backenda stiže na zadnjem koraku, ali ostaje vidljiva i ako se vratiš. */}
        {serverErrors.length > 0 && (
          <div className="m-4 rounded-md border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700 mb-1">Grupa nije spremljena</p>
            <ul className="list-disc pl-5 text-sm text-red-700">
              {serverErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        <Wizard steps={steps} />
      </ModalBody>

      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isSaving}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default PricingSchemeEditCard;
